import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { Camera, Download, ExternalLink, Images, Loader2, Star, Trophy, X } from "lucide-react";
import { Logo } from "./Logo";
import { checkOwner, getSavedPin } from "../lib/auth";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const fmtMB = (bytes) => `${(Number(bytes || 0) / (1024 * 1024)).toFixed(1)} MB`;

// Owner hub, two tabs: "The Gallery" (every booking's photos) and
// "The Hall of Fame" (the owner's starred best shots across all games).
export default function GalleryHub() {
  const [authed, setAuthed] = useState(false);
  const [checking, setChecking] = useState(true);
  const [tab, setTab] = useState("gallery"); // "gallery" | "hall"
  const [galleries, setGalleries] = useState(null);
  const [totals, setTotals] = useState({ total_photos: 0, total_size: 0 });
  const [hall, setHall] = useState(null);
  const [err, setErr] = useState("");

  const load = useCallback(async () => {
    const saved = getSavedPin();
    const headers = saved ? { "x-schedule-pin": saved } : {};
    try {
      const { data } = await axios.get(`${API}/gallery/hub`, { headers });
      setGalleries(data.galleries || []);
      setTotals({ total_photos: data.total_photos || 0, total_size: data.total_size || 0 });
      setErr("");
      return true;
    } catch {
      return false;
    }
  }, []);

  const loadHall = useCallback(async () => {
    const saved = getSavedPin();
    const headers = saved ? { "x-schedule-pin": saved } : {};
    try {
      const { data } = await axios.get(`${API}/gallery/hall-of-fame`, { headers });
      setHall(data.photos || []);
    } catch {
      setHall([]);
    }
  }, []);

  const unstar = async (p) => {
    try {
      await axios.post(`${API}/gallery/${p.booking_id}/file/${p.id}/star`, {}, { headers: getSavedPin() ? { "x-schedule-pin": getSavedPin() } : {} });
      setHall((h) => (h || []).filter((x) => x.id !== p.id));
    } catch {
      setErr("Couldn't remove the star — try again.");
    }
  };

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const owner = await checkOwner();
      const pin = getSavedPin();
      const ok = owner || Boolean(pin);
      const loaded = ok ? await load() : false;
      if (cancelled) return;
      if (!loaded) {
        // Not signed in as owner — the inbox owns the sign-in experience.
        window.location.href = "/inbox";
        return;
      }
      setAuthed(true);
      setChecking(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [load]);

  useEffect(() => {
    if (tab === "hall" && hall === null && authed) loadHall();
  }, [tab, hall, authed, loadHall]);

  if (checking) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-ink font-body text-white" data-testid="gallery-hub-page">
        <p className="flex items-center gap-3 text-sm font-semibold uppercase tracking-[0.2em] text-white/60">
          <Loader2 className="h-5 w-5 animate-spin text-accentlight" /> Opening The Gallery…
        </p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-ink font-body text-white" data-testid="gallery-hub-page">
      <header className="border-b border-white/5 bg-ink/80 backdrop-blur-xl">
        <div className="mx-auto flex h-16 w-full max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <span className="text-accentlight">
              <Logo className="h-8 w-8" />
            </span>
            <div className="leading-none">
              <p className="font-display text-xl tracking-wider">
                THE <span className="text-accentlight">GALLERY</span>
              </p>
              <p className="text-[9px] font-semibold uppercase tracking-[0.4em] text-white/40">
                GameLens Photography
              </p>
            </div>
          </div>
          <a
            href="/inbox"
            data-testid="gallery-hub-inbox-link"
            className="rounded-full border border-white/10 px-4 py-2 text-xs font-semibold text-white/70 transition-colors hover:border-accentlight hover:text-accentlight"
          >
            Booking inbox
          </a>
        </div>
      </header>

      <main className="mx-auto w-full max-w-6xl px-6 py-10">
        <div className="mb-8 flex flex-wrap items-center gap-3" data-testid="gallery-hub-tabs">
          <button
            type="button"
            onClick={() => setTab("gallery")}
            data-testid="gallery-hub-tab-gallery"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${tab === "gallery" ? "bg-gradient-to-r from-accent to-accentlight text-ink" : "border border-white/10 text-white/60 hover:text-white"}`}
          >
            <Images className="h-3.5 w-3.5" /> The Gallery
            {galleries && (
              <span className="text-[10px] opacity-70">{totals.total_photos}</span>
            )}
          </button>
          <button
            type="button"
            onClick={() => setTab("hall")}
            data-testid="gallery-hub-tab-hall"
            className={`inline-flex items-center gap-2 rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-wider transition-all ${tab === "hall" ? "bg-gradient-to-r from-amber-400 to-amber-200 text-ink" : "border border-white/10 text-white/60 hover:text-white"}`}
          >
            <Trophy className="h-3.5 w-3.5" /> The Hall of Fame
            {hall && <span className="text-[10px] opacity-70">{hall.length}</span>}
          </button>
        </div>

        {err && (
          <p className="mb-6 rounded-xl border border-red-400/30 bg-red-500/10 px-4 py-3 text-sm text-red-200" data-testid="gallery-hub-error">
            {err}
          </p>
        )}

        {tab === "gallery" && (
          <>
            <div className="mb-8">
              <h1 className="font-display text-4xl tracking-wider md:text-5xl">
                EVERY <span className="text-accentlight">GALLERY</span>
              </h1>
              <p className="mt-2 text-sm text-white/50" data-testid="gallery-hub-stats">
                {totals.total_photos} photo{totals.total_photos === 1 ? "" : "s"} across{" "}
                {galleries ? galleries.length : 0} game{galleries?.length === 1 ? "" : "s"} ·{" "}
                {fmtMB(totals.total_size)} stored — star a photo in the inbox to send it to the Hall of Fame
              </p>
            </div>

            {galleries && galleries.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-white/10 py-20 text-center" data-testid="gallery-hub-empty">
                <Camera className="h-10 w-10 text-white/20" strokeWidth={1.5} />
                <p className="text-white/50">No galleries yet.</p>
                <p className="text-xs text-white/30">
                  Photos you upload to a booking in the inbox appear here.
                </p>
              </div>
            )}

            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              {(galleries || []).map((g) => (
                <article
                  key={g.booking_id}
                  data-testid={`gallery-hub-card-${g.booking_id}`}
                  className="rounded-2xl border border-white/5 bg-surface p-6"
                >
                  <div className="flex flex-wrap items-start justify-between gap-3">
                    <div>
                      <h2 className="font-display text-xl tracking-wider">
                        {g.team || g.name || "Game"}
                      </h2>
                      <p className="mt-1 text-xs text-white/40">
                        {g.game_date || "No date set"} · {g.name}
                      </p>
                    </div>
                    <span className="inline-flex items-center gap-1.5 rounded-full bg-accentlight/15 border border-accentlight/40 px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-accentlight">
                      <Images className="h-3 w-3" /> {g.photo_count} photos · {fmtMB(g.total_size)}
                    </span>
                  </div>

                  {g.gallery_sent ? (
                    <p className="mt-3 text-[11px] text-emerald-300/80">
                      Gallery email sent — link works for 90 days from delivery.
                    </p>
                  ) : (
                    <p className="mt-3 text-[11px] text-amber-300/80">
                      Photos uploaded — gallery email not sent yet.
                    </p>
                  )}

                  <div className="mt-4 flex flex-wrap gap-3">
                    <a
                      href={g.url}
                      target="_blank"
                      rel="noreferrer"
                      data-testid={`gallery-hub-open-${g.booking_id}`}
                      className="inline-flex items-center gap-2 rounded-full bg-gradient-to-r from-accent to-accentlight px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-ink transition-all hover:brightness-110"
                    >
                      <ExternalLink className="h-3.5 w-3.5" /> Open gallery
                    </a>
                    <a
                      href={g.zip_url}
                      download
                      data-testid={`gallery-hub-zip-${g.booking_id}`}
                      className="inline-flex items-center gap-2 rounded-full border border-accentlight/40 px-5 py-2.5 text-xs font-bold uppercase tracking-wider text-accentlight transition-all hover:bg-accentlight/10"
                    >
                      <Download className="h-3.5 w-3.5" /> Download all
                    </a>
                  </div>
                </article>
              ))}
            </div>
          </>
        )}

        {tab === "hall" && (
          <>
            <div className="mb-8">
              <h1 className="font-display text-4xl tracking-wider md:text-5xl">
                HALL OF <span className="text-amber-300">FAME</span>
              </h1>
              <p className="mt-2 text-sm text-white/50" data-testid="hall-of-fame-stats">
                {hall ? hall.length : "…"} favorite photo{(hall || []).length === 1 ? "" : "s"} across all your games — your best work, in one place
              </p>
            </div>

            {hall && hall.length === 0 && (
              <div className="flex flex-col items-center justify-center gap-4 rounded-2xl border border-dashed border-amber-300/20 py-20 text-center" data-testid="hall-of-fame-empty">
                <Star className="h-10 w-10 text-amber-300/30" strokeWidth={1.5} />
                <p className="text-white/50">No favorites yet.</p>
                <p className="text-xs text-white/30">
                  Tap the star on any photo in The Gallery (in your inbox) and it lands here.
                </p>
              </div>
            )}

            <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4">
              {(hall || []).map((p) => (
                <figure
                  key={p.id}
                  data-testid={`hall-photo-${p.id}`}
                  className="group relative overflow-hidden rounded-xl border border-amber-300/20 bg-surface"
                >
                  <a href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`} target="_blank" rel="noreferrer">
                    <img
                      src={`${process.env.REACT_APP_BACKEND_URL}${p.thumb_url}`}
                      alt={p.team || p.name || "Hall of Fame photo"}
                      loading="lazy"
                      className="aspect-square w-full object-cover transition-transform duration-500 group-hover:scale-105"
                    />
                  </a>
                  <span className="absolute left-2 top-2 flex h-7 w-7 items-center justify-center rounded-full bg-ink/85">
                    <Star className="h-3.5 w-3.5 fill-amber-300 text-amber-300" />
                  </span>
                  <figcaption className="flex items-center justify-between gap-2 px-3 py-2">
                    <span className="truncate text-[10px] font-semibold uppercase tracking-[0.12em] text-white/50">
                      {p.team || p.name || "Game"}
                      {p.game_date ? ` · ${p.game_date.split(",")[0]}` : ""}
                    </span>
                    <span className="flex shrink-0 items-center gap-1">
                      <a
                        href={`${process.env.REACT_APP_BACKEND_URL}${p.url}`}
                        download
                        aria-label="Download photo"
                        data-testid={`hall-download-${p.id}`}
                        className="rounded-md p-1 text-white/60 transition-colors hover:text-accentlight"
                      >
                        <Download className="h-3.5 w-3.5" />
                      </a>
                      <button
                        type="button"
                        onClick={() => unstar(p)}
                        aria-label="Remove from the Hall of Fame"
                        data-testid={`hall-unstar-${p.id}`}
                        title="Remove from the Hall of Fame"
                        className="rounded-md p-1 text-white/60 transition-colors hover:text-red-300"
                      >
                        <X className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </figcaption>
                </figure>
              ))}
            </div>
          </>
        )}
      </main>
    </div>
  );
}
